// @react/components/Input.tsx
// Auto-generated by corebuilder. DO NOT EDIT.

import React, { forwardRef, useId } from 'react';
import { cn } from '@core/ui';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input label text */
  label?: string;
  /** Helper text displayed below input */
  helperText?: string;
  /** Error message - shows error state when provided */
  error?: string;
  /** Input size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Icon to display at the start of input */
  leftIcon?: React.ReactNode;
  /** Icon to display at the end of input */
  rightIcon?: React.ReactNode;
  /** Make input take full width */
  fullWidth?: boolean;
}

/**
 * Input component with label, helper text, and error states.
 * Uses CSS classes from @core/ui for consistent styling.
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   error={errors.email}
 * />
 *
 * <Input
 *   label="Search"
 *   leftIcon={<SearchIcon />}
 *   placeholder="Search..."
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      size = 'md',
      leftIcon,
      rightIcon,
      fullWidth = true,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = !!error;

    return (
      <div className={cn('form-group', fullWidth && 'form-group-full', className)}>
        {label && (
          <label htmlFor={inputId} className="label">
            {label}
            {props.required && <span className="label-required">*</span>}
          </label>
        )}
        <div className="input-wrapper">
          {leftIcon && <span className="input-icon input-icon-left">{leftIcon}</span>}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'input',
              `input-${size}`,
              hasError && 'input-error',
              leftIcon ? 'input-with-left-icon' : false,
              rightIcon ? 'input-with-right-icon' : false
            )}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />
          {rightIcon && <span className="input-icon input-icon-right">{rightIcon}</span>}
        </div>
        {hasError && (
          <span id={`${inputId}-error`} className="input-error-text" role="alert">
            {error}
          </span>
        )}
        {!hasError && helperText && (
          <span id={`${inputId}-helper`} className="input-helper-text">
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Textarea variant
export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  helperText?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, helperText, error, size = 'md', fullWidth = true, id, ...props }, ref) => {
    const generatedId = useId();
    const textareaId = id || generatedId;
    const hasError = !!error;

    return (
      <div className={cn('form-group', fullWidth && 'form-group-full', className)}>
        {label && (
          <label htmlFor={textareaId} className="label">
            {label}
            {props.required && <span className="label-required">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={cn('textarea', `textarea-${size}`, hasError && 'textarea-error')}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
          }
          {...props}
        />
        {hasError && (
          <span id={`${textareaId}-error`} className="input-error-text" role="alert">
            {error}
          </span>
        )}
        {!hasError && helperText && (
          <span id={`${textareaId}-helper`} className="input-helper-text">
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
